/**
 * WebAuthn MFA Setup Component
 *
 * Handles WebAuthn passkey setup for MFA verification.
 * This component manages the complete WebAuthn registration flow.
 */

'use client';

import React, { useState, useCallback } from 'react';
import { Button, Card, Typography, Alert, Steps, message, Spin } from 'antd';
import {
  KeyOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '@/domains/auth/context/auth-context';
import { WebAuthnService } from '@/domains/auth/services/webauthn-service';

const { Title, Text, Paragraph } = Typography;
interface WebAuthnMFASetupProps {
  onComplete?: () => void;
  onCancel?: () => void;
}

export const WebAuthnMFASetup: React.FC<WebAuthnMFASetupProps> = ({
  onComplete,
  onCancel
}) => {
  const { user, verifyMFACredential } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrationOptions, setRegistrationOptions] = useState<any>(null);
  const [webauthnService] = useState(() => new WebAuthnService());

  const steps = [
    {
      title: 'Check Support',
      content: 'Verify WebAuthn support',
      icon: <SafetyOutlined />
    },
    {
      title: 'Create Passkey',
      content: 'Set up your device passkey',
      icon: <KeyOutlined />
    },
    {
      title: 'Verify Setup',
      content: 'Confirm passkey is working',
      icon: <CheckCircleOutlined />
    }
  ];

  const checkWebAuthnSupport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if WebAuthn is supported
      if (!webauthnService.isSupported()) {
        throw new Error('WebAuthn is not supported in this browser');
      }

      // Check if passkeys are supported
      const isPasskeySupported = await webauthnService.isPasskeySupported();
      if (!isPasskeySupported) {
        throw new Error('Passkeys are not supported on this device');
      }

      setCurrentStep(1);
      message.success('WebAuthn is supported on your device');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to check WebAuthn support');
      message.error('WebAuthn is not supported on your device');
    } finally {
      setLoading(false);
    }
  }, [webauthnService]);

  const startRegistration = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user?.username) {
        throw new Error('User information not available');
      }

      // Start WebAuthn registration
      const options = await webauthnService.startRegistration(
        user.username,
        `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username
      );

      setRegistrationOptions(options);
      setCurrentStep(2);
      message.success('Passkey registration started');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to start passkey registration');
      message.error('Failed to start passkey registration');
    } finally {
      setLoading(false);
    }
  }, [webauthnService, user]);

  const completeRegistration = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!registrationOptions) {
        throw new Error('Registration options not available');
      }

      // Complete WebAuthn registration
      const success = await webauthnService.completeRegistration(registrationOptions);

      if (success) {
        // Verify the credential with the MFA service
        const credential = await webauthnService.getLastCredential();
        if (credential) {
          const result = await verifyMFACredential(credential);

          if (result.success) {
            setCurrentStep(3);
            message.success('Passkey setup completed successfully!');

            // Call completion callback
            if (onComplete) {
              onComplete();
            }
          } else {
            throw new Error(result.error || 'MFA verification failed');
          }
        } else {
          throw new Error('No credential available for verification');
        }
      } else {
        throw new Error('Passkey registration failed');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to complete passkey registration');
      message.error('Failed to complete passkey registration');
    } finally {
      setLoading(false);
    }
  }, [webauthnService, registrationOptions, verifyMFACredential, onComplete]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <Title level={3}>Check WebAuthn Support</Title>
            <Paragraph>
              We'll check if your device supports WebAuthn passkeys for secure authentication.
            </Paragraph>
            <Button
              type="primary"
              size="large"
              onClick={checkWebAuthnSupport}
              loading={loading}
            >
              Check Support
            </Button>
          </div>
        );

      case 1:
        return (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <Title level={3}>Create Your Passkey</Title>
            <Paragraph>
              Your device will prompt you to create a passkey. This will be used for secure authentication.
            </Paragraph>
            <Button
              type="primary"
              size="large"
              onClick={startRegistration}
              loading={loading}
            >
              Create Passkey
            </Button>
          </div>
        );

      case 2:
        return (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <Title level={3}>Complete Passkey Setup</Title>
            <Paragraph>
              Your device should have prompted you to create a passkey. Click below to complete the setup.
            </Paragraph>
            <Button
              type="primary"
              size="large"
              onClick={completeRegistration}
              loading={loading}
            >
              Complete Setup
            </Button>
          </div>
        );

      case 3:
        return (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <Title level={3}>Setup Complete!</Title>
            <Paragraph>
              Your passkey has been successfully set up and verified. You can now use it for secure authentication.
            </Paragraph>
            <Button
              type="primary"
              size="large"
              onClick={onComplete}
            >
              Continue
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card style={{ maxWidth: 600, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <Steps current={currentStep} items={steps} />
      </div>

      {error && (
        <Alert
          title="Setup Error"
          description={error}
          type="error"
          icon={<ExclamationCircleOutlined />}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Processing...</Text>
          </div>
        </div>
      ) : (
        renderStepContent()
      )}

      {onCancel && currentStep < 3 && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Button onClick={onCancel}>
            Cancel Setup
          </Button>
        </div>
      )}
    </Card>
  );
};
